import {
  NoteListItemDto as ApiNoteListItemDto,
  GetNoteResponse,
  CreateNoteCommand,
  UpdateNoteCommand,
  ReferentielMinimalDto
} from 'api/note';
import { NoteListItemDto, NoteFormValues } from '../types/interface';

/**
 * Map API NoteListItemDto or GetNoteResponse to UI NoteListItemDto
 */
export const mapApiNoteToUiNote = (
  apiNote: ApiNoteListItemDto | GetNoteResponse
): NoteListItemDto => {
  const noteResponse = apiNote as GetNoteResponse;
  return {
    ...apiNote,
    displayDate:
      (apiNote as ApiNoteListItemDto).relativeTime ||
      formatNoteDate(apiNote.creationTime),
    displayTags: apiNote.tags?.map((tag) => tag.label || '') || [],
    // Extended fields from GetNoteResponse
    person: noteResponse.person,
    risk: noteResponse.risk,
    broker: noteResponse.broker,
    content: noteResponse.content,
    attachments: noteResponse.attachments
  };
};

/**
 * Map array of API notes to UI notes
 */
export const mapApiNotesToUiNotes = (
  apiNotes: ApiNoteListItemDto[]
): NoteListItemDto[] => {
  return apiNotes.map(mapApiNoteToUiNote);
};

/**
 * Map UI NoteFormValues to API CreateNoteCommand
 */
export const mapFormValuesToCreateCommand = (
  values: NoteFormValues,
  riskId?: string,
  personId?: string
): CreateNoteCommand => {
  return {
    title: values.title,
    content: values.content || undefined,
    dueDate: values.dueDate || undefined,
    priorityId: values.priorityId || '',
    statusId: values.statusId || '',
    tagIds: values.tagIds,
    mentionPersonIds: values.mentionPersonIds,
    attachmentIds: values.attachmentIds,
    riskId: riskId || values.riskId || undefined,
    personId: personId || values.personId || undefined
  };
};

/**
 * Map UI NoteFormValues to API UpdateNoteCommand
 */
export const mapFormValuesToUpdateCommand = (
  values: NoteFormValues,
  riskId?: string,
  personId?: string
): UpdateNoteCommand => {
  return {
    title: values.title,
    content: values.content || undefined,
    dueDate: values.dueDate || undefined,
    priorityId: values.priorityId || '',
    statusId: values.statusId || '',
    tagIds: values.tagIds,
    mentionPersonIds: values.mentionPersonIds,
    attachmentIds: values.attachmentIds,
    riskId: riskId || values.riskId || undefined,
    personId: personId || values.personId || undefined
  };
};

/**
 * Map GetNoteResponse to NoteFormValues for editing
 */
export const mapNoteToFormValues = (note: GetNoteResponse): NoteFormValues => {
  return {
    title: note.title || '',
    content: note.content || '',
    dueDate: note.dueDate || null,
    priorityId: note.priority?.id,
    statusId: note.status?.id,
    tagIds: note.tags?.map((tag) => tag.id || '').filter(Boolean) || [],
    mentionPersonIds: note.mentions?.map((m) => m.id || '').filter(Boolean) || [],
    attachmentIds: note.attachments?.map((a) => a.id || '').filter(Boolean) || [],
    riskId: note.risk?.id,
    personId: note.person?.id,
    tags: note.tags,
    mentions: note.mentions
  };
};

/**
 * Map NoteListItemDto (list item) to NoteFormValues for quick editing
 */
export const mapListItemToFormValues = (note: NoteListItemDto): NoteFormValues => {
  return {
    title: note.title || '',
    content: note.content || '',
    dueDate: note.dueDate || null,
    priorityId: note.priority?.id,
    statusId: note.status?.id,
    tagIds: note.tags?.map((tag) => tag.id || '').filter(Boolean) || [],
    mentionPersonIds: note.mentions?.map((m) => m.id || '').filter(Boolean) || [],
    attachmentIds: note.attachments?.map((a) => a.id || '').filter(Boolean) as string[] || [],
    // Liens existants de la note (personne / dossier) : à préserver lors d'un update,
    // sinon le back délie la note au premier enregistrement.
    riskId: note.risk?.id,
    personId: note.person?.id,
    tags: note.tags,
    mentions: note.mentions
  };
};

/**
 * Get initial empty form values
 */
export const getInitialFormValues = (): NoteFormValues => {
  return {
    title: '',
    content: '',
    dueDate: null,
    priorityId: undefined,
    statusId: undefined,
    tagIds: [],
    mentionPersonIds: [],
    attachmentIds: [],
    tags: [],
    mentions: []
  };
};

/**
 * Format date for display
 */
export const formatNoteDate = (date?: Date): string => {
  if (!date) return '';

  const noteDate = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - noteDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return noteDate.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (diffDays < 7) {
    return noteDate.toLocaleDateString('fr-FR', {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  return noteDate.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Helper to convert hex to rgba
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface DisplayConfig {
  label: string;
  color?: string;
  bgColor?: string;
  textColor?: string;
  style?: {
    backgroundColor: string;
    color: string;
  };
}

export type PriorityDisplayConfig = DisplayConfig;
export type StatusDisplayConfig = DisplayConfig;

/**
 * Get priority display config from ReferentielMinimalDto
 * Uses the color from the referential (database) if available
 */
export const getPriorityConfig = (
  priority?: ReferentielMinimalDto
): PriorityDisplayConfig | null => {
  if (!priority) return null;

  // Use color from database if available
  if (priority.color) {
    return {
      label: priority.label || priority.code || '',
      color: priority.color,
      // Inline styles for hex colors from database
      style: {
        backgroundColor: hexToRgba(priority.color, 0.1),
        color: priority.color
      }
    };
  }

  // Fallback for priorities without color
  return {
    label: priority.label || priority.code || '',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-600',
    color: undefined
  };
};

/**
 * Get status display config from ReferentielMinimalDto
 * Uses the color from the referential (database) if available
 */
export const getStatusConfig = (
  status?: ReferentielMinimalDto
): StatusDisplayConfig | null => {
  if (!status) return null;

  // Use color from database if available
  if (status.color) {
    return {
      label: status.label || status.code || '',
      color: status.color,
      // Inline styles for hex colors from database
      style: {
        backgroundColor: hexToRgba(status.color, 0.1),
        color: status.color
      }
    };
  }

  // Fallback for statuses without color
  return {
    label: status.label || status.code || '',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-600',
    color: undefined
  };
};
